import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { exportResumenByOrder, exportResumenByProduct } from '../utils/exportOrders';
import KpiCard from './KpiCard';
import DateRangeFilter, { resolvePreset } from './ui/DateRangeFilter';
import {
  fmtMoney,
  getPaymentLabel,
  normalizeSalesChannel,
  RESUMEN_STATUSES,
} from '../utils/orderUtils';
import { colRangeParams } from '../utils/colTimeUtils';
import { decodeHtml } from '../utils/decodeHtml';
import { groupOrdersByProduct } from '../utils/groupOrdersByProduct';
import useSalesChannels from '../hooks/useSalesChannels';
import EmptyState from './EmptyState';
import FotoProducto from './FotoProducto';
import EspejoFranja from './EspejoFranja';
import CapturaPosCard from './CapturaPosCard'; // pos-pedir-celular: % de captura de datos del POS
import OrderListInspector from './orders/OrderListInspector';
import OrderEditPage from './orders/OrderEditPage';
import './SalesResumen.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const ORDERS_VISIBLE_DEFAULT = 15;


const CASH_RE = /(efectivo|cash|contra\s*entrega|\bcod\b)/i;
const TRANSFER_RE = /(transfer|transferencia|banco|bank|bancolombia)/i;

function getOrderCanal(order) {
  return normalizeSalesChannel(order).key;
}

function computeKpis(orders) {
  const count = orders.length;
  const totalRevenue = orders.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
  return { count, totalRevenue, avgTicket: count > 0 ? totalRevenue / count : 0 };
}

// La agrupación por producto padre vive en `utils/groupOrdersByProduct.js`, MÓDULO
// COMPARTIDO con la tool `consultar_ventas` del MCP (backend). Estaba inline acá y el
// backend la reimplementaba agrupando por nombre crudo: dos top-5 distintos sobre los
// mismos datos. El porqué completo, y por qué NO es un espejo, en el header del módulo.

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
  });
}

function formatPeriodLabel(start, end) {
  if (!start || !end) return 'Sin periodo';
  if (start === end) return formatDisplayDate(start);
  return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
}

function parsePaymentAmount(value) {
  let cleaned = String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^0-9,.-]/g, '');
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    cleaned = lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (lastDot >= 0) {
    const parts = cleaned.split('.');
    cleaned = parts.length > 1 && parts[parts.length - 1].length === 3
      ? parts.join('')
      : cleaned;
  } else if (lastComma >= 0) {
    const parts = cleaned.split(',');
    cleaned = parts.length > 1 && parts[parts.length - 1].length === 3
      ? parts.join('')
      : cleaned.replace(',', '.');
  }

  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : 0;
}

function normalizePaymentMethod(label) {
  const clean = String(label || '').replace(/\s+/g, ' ').trim();
  if (!clean) return 'No registrado';
  if (CASH_RE.test(clean)) return 'Efectivo';
  if (TRANSFER_RE.test(clean)) return 'Transferencia';
  if (/mixto/i.test(clean)) return 'Pago mixto';
  return clean;
}

function getPaymentParts(order) {
  const payment = getPaymentLabel(order);
  if (payment.parts?.length) {
    const parts = payment.parts
      .map(part => ({
        method: normalizePaymentMethod(part.method),
        amount: parsePaymentAmount(part.amount),
      }))
      .filter(part => part.amount > 0);
    if (parts.length > 0) return parts;
  }

  return [{
    method: normalizePaymentMethod(payment.label),
    amount: parseFloat(order.total) || 0,
  }];
}

function computePaymentSummary(orders) {
  const map = new Map();

  for (const order of orders) {
    const methodsInOrder = new Set();
    for (const part of getPaymentParts(order)) {
      const current = map.get(part.method) || { method: part.method, amount: 0, orders: 0 };
      current.amount += part.amount;
      map.set(part.method, current);
      methodsInOrder.add(part.method);
    }
    for (const method of methodsInOrder) {
      const current = map.get(method);
      if (current) current.orders += 1;
    }
  }

  const list = Array.from(map.values()).sort((a, b) => b.amount - a.amount || b.orders - a.orders);
  const cash = map.get('Efectivo') || { amount: 0, orders: 0 };
  const transfer = map.get('Transferencia') || { amount: 0, orders: 0 };
  return {
    list,
    top: list[0] || null,
    cashTotal: cash.amount,
    cashCount: cash.orders,
    transferTotal: transfer.amount,
    transferCount: transfer.orders,
    total: list.reduce((sum, item) => sum + item.amount, 0),
  };
}

function plural(n, singular, pluralText = `${singular}s`) {
  return n === 1 ? singular : pluralText;
}

function Skeleton() {
  return (
    <div className="sr-skeleton-grid" aria-label="Cargando resumen">
      <div className="skeleton sr-skeleton-card" />
      <div className="skeleton sr-skeleton-card" />
      <div className="skeleton sr-skeleton-card" />
    </div>
  );
}

export default function SalesResumen({ onReady, userRole = 'vendedor', registerControls, onNavigate }) {
  // Período al molde (F5): DateRangeFilter es la ÚNICA fuente del rango.
  // dateRange = { preset, from, to }; rangeStart/rangeEnd derivan de él. El GATE
  // de fechas (backend/tmp-verify-resumen-fechas.js) probó que cada preset da el
  // MISMO rango que el calendario viejo → los números NO cambian.
  const [dateRange, setDateRange] = useState(() => ({ preset: 'today', ...resolvePreset('today') }));
  const rangeStart = dateRange.from;
  const rangeEnd = dateRange.to;

  const [orders, setOrders] = useState([]);
  // W3: frescura del espejo de pedidos (viaja en cada respuesta de /api/orders).
  const [espejoInfo, setEspejoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState(null);

  const [selected, setSelected] = useState(null); // pedido abierto en el drawer compartido
  const [fullPage, setFullPage] = useState(null); // { order } — edición embebida (criterio 17)

  const [canalFilter, setCanalFilter] = useState('all');
  const { channels: salesChannels } = useSalesChannels({ includeArchived: true });
  // Canales presentes en los pedidos DEL PERÍODO. Sale de `orders`, que ya está en
  // memoria (los mismos pedidos que alimentan los totales) — SIN consulta extra. Es
  // el conjunto de `canal_venta` realmente usados en la ventana consultada; cambia
  // solo cuando cambia el período (refetch), y de ahí sale el borde del filtro.
  const presentCanales = useMemo(
    () => new Set(orders.map(getOrderCanal)),
    [orders],
  );
  // Un canal ACTIVO se ofrece siempre. Uno ARCHIVADO (active === false) solo si tiene
  // al menos un pedido en el período: así una feria archivada con historial real sigue
  // filtrable en su mes, y un canal de prueba archivado sin ventas (el «Prueba modal
  // 22-ago», 0 pedidos) no aparece como píldora muerta. NO se esconden todos los
  // archivados a propósito: eso le sacaría a Isa el filtro de los meses con feria.
  const canalOptions = useMemo(() => ([
    { key: 'all', label: 'Todos los canales' },
    ...salesChannels
      .filter((c) => c.active !== false || presentCanales.has(c.name))
      .map((c) => ({ key: c.name, label: c.name })),
  ]), [salesChannels, presentCanales]);
  const [productFilter, setProductFilter] = useState('all');
  // CUATRO estados distinguibles, a propósito: 'cargando' | 'ok' | 'sin-negocio' | 'error'.
  // Colapsarlos en un `null` es la forma ④ —el dato que FALTA se lee igual que el dato
  // que dice «no hay»— y esta tanda existe para arreglar exactamente eso. Con 'error' la
  // píldora NO desaparece: queda deshabilitada diciendo por qué (regla de la casa:
  // degradación cuando falta un dato, nunca desaparición silenciosa).
  const [biz, setBiz] = useState({ estado: 'cargando', negocio: null });
  const beautyBiz = biz.negocio;
  const beautyIds = beautyBiz?.ids ?? null;
  const bizName = beautyBiz?.name || '';
  const onReadyCalledRef = useRef(false);
  const [storeName, setStoreName] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(cfg => { if (cfg?.negocio?.nombre) setStoreName(cfg.negocio.nombre); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // La definición de «qué productos son del negocio» viene del backend
  // (/api/businesses/product-ids) — acá vivía una copia hardcodeada de las
  // categorías ([123,128]) y divergió del catálogo real cuando se reorganizó
  // (bug del labial 8908, 12-ago-2026).
  // Un HTTP no-ok o un cuerpo sin `businesses[]` van a 'error', NO a «no hay negocio»:
  // son cosas distintas y tienen que verse distinto.
  useEffect(() => {
    fetch(`${API_URL}/api/businesses/product-ids`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!Array.isArray(d?.businesses)) throw new Error('respuesta sin businesses[]');
        // [0] NO es «el primero» en ningún sentido del negocio: es EL QUE VENGA PRIMERO
        // en la respuesta. Hoy el orden lo fija `getBusinesses` con .sort({isBase:-1,
        // name:1}) (services/negocios.js:30), o sea el no-base de nombre alfabéticamente
        // menor — un criterio que nadie eligió y que cambia solo con renombrar. Hoy da
        // igual porque hay UN no-base; el día del segundo, esta línea elige en silencio.
        // Eso es tanda propia (N píldoras), no de acá.
        const b = d.businesses[0] || null;
        setBiz(b
          ? { estado: 'ok', negocio: { key: b.key, name: b.name, ids: new Set(b.ids) } }
          : { estado: 'sin-negocio', negocio: null });
      })
      .catch(() => setBiz({ estado: 'error', negocio: null }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canalFilteredOrders = useMemo(() => {
    if (canalFilter === 'all') return orders;
    return orders.filter(o => getOrderCanal(o) === canalFilter);
  }, [orders, canalFilter]);

  const kpis = useMemo(() => computeKpis(canalFilteredOrders), [canalFilteredOrders]);
  const grouped = useMemo(() => groupOrdersByProduct(canalFilteredOrders), [canalFilteredOrders]);
  const paymentSummary = useMemo(() => computePaymentSummary(canalFilteredOrders), [canalFilteredOrders]);

  const totalQty = useMemo(() => grouped.reduce((s, p) => s + p.qty, 0), [grouped]);
  const beautyQty = useMemo(() => {
    if (beautyIds === null) return null;
    return grouped
      .filter(p => beautyIds.has(p.productId))
      .reduce((s, p) => s + p.qty, 0);
  }, [grouped, beautyIds]);

  const groupedFiltered = useMemo(() => {
    if (productFilter === 'all') return grouped;
    if (beautyIds === null) return [];
    return grouped.filter(p => beautyIds.has(p.productId));
  }, [grouped, productFilter, beautyIds]);

  const sortedOrders = useMemo(
    () => [...canalFilteredOrders].sort((a, b) => new Date(b.date_created) - new Date(a.date_created)),
    [canalFilteredOrders]
  );
  const visibleOrders = showAll ? sortedOrders : sortedOrders.slice(0, ORDERS_VISIBLE_DEFAULT);
  const hiddenCount = sortedOrders.length - ORDERS_VISIBLE_DEFAULT;

  // `fresh` salta la caché de 30 min del backend (nocache=1) — es lo que aprieta
  // el usuario cuando una venta recién entró y no la ve. P3-11-A: sin esto, el
  // botón Actualizar re-pedía la MISMA clave cacheada y la venta nueva no aparecía
  // (Pedidos sí lo hacía, OrdersPage.js:153 — por eso ahí sí se veía). La carga
  // automática (cambio de período) sigue usando caché: el refresco explícito es lo
  // único que la salta, igual que en Pedidos.
  const fetchData = useCallback(async (after, before, { fresh = false } = {}) => {
    if (!after || !before) return;
    setLoading(true);
    setError(null);
    setFetched(true);
    setShowAll(false);
    try {
      const all = [];
      let p = 1;
      const { after: afterTs, before: beforeTs } = colRangeParams(after, before);
      while (p <= 10) {
        const params = new URLSearchParams({
          status: 'any',
          per_page: 100,
          page: p,
          after:  afterTs,
          before: beforeTs,
        });
        if (fresh) params.set('nocache', '1');
        const res = await fetch(`${API_URL}/api/orders?${params}`);
        if (!res.ok) throw new Error('Error al obtener pedidos');
        const data = await res.json();
        if (p === 1) setEspejoInfo({ hace: data.espejo_actualizado_hace_seg ?? null, advertencia: data.espejo_advertencia || null, vacio: data.espejo_vacio === true, migrando: data.espejo_migrando === true }); // W3
        all.push(...(data.orders || []));
        if (p >= (data.total_pages || 1)) break;
        p++;
      }
      // S2b: los pedidos RECLASIFICADOS ("Descuento 100%" → retiro de inventario) dejaron de ser
      // ventas — el backend los marca (`reclasificado`) y acá se excluyen del resumen, para que
      // ingresos/COGS/margen coincidan con Finanzas, Analítica y el asistente (no doble conteo).
      const filtered = all.filter(o => RESUMEN_STATUSES.has(o.status) && !o.reclasificado);
      console.log(`[SalesResumen] ${all.length} pedidos totales -> ${filtered.length} con estado de ingreso`);
      if (filtered.length > 0) {
        const sample = filtered[0];
        console.log(`[SalesResumen] primer pedido #${sample.number}: ${(sample.line_items || []).length} line_items`, (sample.line_items || []).map(i => `pid=${i.product_id} vid=${i.variation_id} qty=${i.quantity}`));
      }
      setOrders(filtered);
      if (!onReadyCalledRef.current && onReady) {
        onReadyCalledRef.current = true;
        onReady();
      }
    } catch (err) {
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [onReady]);

  useEffect(() => {
    fetchData(rangeStart, rangeEnd);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // P3-11-B: refresco EN VIVO. Cuando entra/cambia un pedido (venta POS al instante,
  // webhook o reconciliador), el backend emite 'orders' por SSE y App.js lo reemite como
  // 'app:data-changed'. Resumen se actualiza solo, sin que el usuario apriete Actualizar —
  // «la venta del POS entra a Resumen al instante». Fresh (nocache) porque el interés es
  // justamente ver lo recién entrado. `liveRef` evita el closure viejo del rango sin
  // re-suscribir el listener en cada cambio de período (patrón de valor-más-reciente).
  const liveRef = useRef(null);
  liveRef.current = { from: rangeStart, to: rangeEnd, fetch: fetchData };
  useEffect(() => {
    const onChange = (e) => {
      const types = e.detail?.types;
      if (!Array.isArray(types) || !types.includes('orders')) return;
      const { from, to, fetch } = liveRef.current || {};
      if (from && to && fetch) fetch(from, to, { fresh: true });
    };
    window.addEventListener('app:data-changed', onChange);
    return () => window.removeEventListener('app:data-changed', onChange);
  }, []);

  const handleRange = useCallback((v) => {
    setDateRange(v);
    setShowAll(false);
    fetchData(v.from, v.to);
  }, [fetchData]);

  const handleSearch = useCallback(() => {
    fetchData(rangeStart, rangeEnd, { fresh: true }); // P3-11-A: Actualizar salta la caché (como Pedidos)
  }, [rangeStart, rangeEnd, fetchData]);

  const handleBeautyFilter = useCallback(() => {
    if (beautyQty === 0) return;
    setProductFilter('beauty');
  }, [beautyQty]);

  // Handlers del drawer compartido (F5 Tema 3): ver en drawer, editar embebido,
  // gestionar envío = navegar a Pedidos, cancelar = sale del resumen.
  const handleEditOrder = useCallback((o) => setFullPage({ order: o }), []);
  const handleManageShipping = useCallback(() => onNavigate?.('orders', 'shipping'), [onNavigate]);
  const handleOrderCancelled = useCallback((id) => {
    // Cancelado ∉ venta real: sale del resumen (y de sus KPIs). Cierra el drawer.
    setOrders((prev) => prev.filter((o) => o.id !== id));
    setSelected(null);
  }, []);

  const canalLabel = canalOptions.find((c) => c.key === canalFilter)?.label || 'Todos los canales';
  const periodLabel = formatPeriodLabel(rangeStart, rangeEnd);

  const exportOpts = useMemo(() => ({
    storeName,
    periodLabel,
    canalLabel,
    beautyIds,
    beautyLabel: bizName,
    productFilter,
  }), [storeName, periodLabel, canalLabel, beautyIds, bizName, productFilter]);

  const handleExportByOrder   = useCallback(() => exportResumenByOrder(canalFilteredOrders, exportOpts),   [canalFilteredOrders, exportOpts]);
  const handleExportByProduct = useCallback(() => exportResumenByProduct(canalFilteredOrders, exportOpts), [canalFilteredOrders, exportOpts]);

  // Cápsula Actualizar·Exportar al PageHeader del wrapper (F5). El menú de export
  // (Por pedido / Por producto) lo arma el wrapper con estos callbacks.
  useEffect(() => {
    registerControls?.({
      onRefresh: handleSearch,
      refreshing: loading,
      canExport: fetched && !loading && canalFilteredOrders.length > 0,
      onExportByOrder: handleExportByOrder,
      onExportByProduct: handleExportByProduct,
    });
    return () => registerControls?.(null);
  }, [registerControls, handleSearch, loading, fetched, canalFilteredOrders.length, handleExportByOrder, handleExportByProduct]);

  const beautyChipLabel = `${bizName}${fetched ? ` (${beautyQty ?? '...'})` : ''}`;
  const allChipLabel = `Todos los productos${fetched ? ` (${totalQty})` : ''}`;
  const beautyDisabled = fetched && beautyQty === 0;

  const showCashKpi = paymentSummary.cashTotal > 0;
  const showTransferKpi = paymentSummary.transferTotal > 0;

  return (
    <>
    {/* La lista queda MONTADA (oculta) mientras se edita un pedido → "Volver"
       conserva período/canal/scroll (criterio 17, F5 Tema 3, Opción A). */}
    <div className="sr-wrap" style={fullPage ? { display: 'none' } : undefined}>
      {/* W3: frescura de los pedidos sincronizados — informativa + CTA si está vacío */}
      <EspejoFranja
        espejoInfo={espejoInfo}
        sujeto="pedidos"
        onMigrarEspejo={async () => {
          const r = await fetch(`${API_URL}/api/espejo-pedidos/migracion`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
          if (!r.ok) throw new Error('No se pudo arrancar la sincronización');
          return r.json();
        }}
      />
      <section className="sr-controls">
        <div className="sr-controls-row">
          <DateRangeFilter value={dateRange} onChange={handleRange} />
          <span className="sr-transparencia">Incluye pedidos completados y en preparación.</span>
        </div>

        <div className="sr-chip-row" aria-label="Filtros de canal">
          {canalOptions.map(c => (
            <button
              key={c.key}
              type="button"
              className={`sr-canal-chip${canalFilter === c.key ? ' sr-canal-chip--on' : ''}`}
              onClick={() => { setCanalFilter(c.key); setShowAll(false); }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="sr-chip-row">
          <button
            type="button"
            className={`sr-filter-pill${productFilter === 'all' ? ' sr-filter-pill--active' : ''}`}
            onClick={() => setProductFilter('all')}
          >
            {allChipLabel}
          </button>
          {biz.estado === 'ok' && (
            <button
              type="button"
              className={`sr-filter-pill${productFilter === 'beauty' ? ' sr-filter-pill--active' : ''}${beautyDisabled ? ' sr-filter-pill--disabled' : ''}`}
              onClick={handleBeautyFilter}
              disabled={beautyDisabled}
              title={beautyDisabled ? `No hay ventas de ${bizName} en este periodo` : undefined}
            >
              {beautyChipLabel}
            </button>
          )}
          {/* El filtro se CAYÓ (no «no existe»): se ve, deshabilitado, y el title dice
              qué pasó. Antes esto era indistinguible de una tienda sin negocios. */}
          {biz.estado === 'error' && (
            <button
              type="button"
              className="sr-filter-pill sr-filter-pill--disabled"
              disabled
              title="No se pudo cargar la definición del negocio. Los totales de «Todos los productos» son correctos; solo falta poder filtrar. Recargá la página."
            >
              Filtro por negocio no disponible
            </button>
          )}
        </div>
      </section>

      {error && <div className="sr-error surface">{error}</div>}

      {!fetched && !loading && (
        <div className="sr-hint surface-soft">No hay información suficiente para generar este resumen.</div>
      )}

      {loading && <Skeleton />}

      {fetched && !loading && orders.length === 0 && !error && (
        <EmptyState
          icon="📋"
          title="No hay ventas en este periodo."
          description="Prueba otro periodo o revisa si hay pedidos pendientes por confirmar."
        />
      )}

      {fetched && !loading && orders.length > 0 && canalFilteredOrders.length === 0 && !error && (
        <div className="sr-hint surface-soft">
          No hay ventas para {canalLabel} en este periodo.
        </div>
      )}

      {fetched && !loading && canalFilteredOrders.length > 0 && (
        <>
          <section className="page-section">
            <h2 className="page-section-title">Resumen simple</h2>
            <div className="sr-kpi-row module-kpi-row">
              <KpiCard
                icon="💰"
                tone="success"
                label={`Ventas del periodo${canalFilter !== 'all' ? ` - ${canalLabel}` : ''}`}
                value={fmtMoney(kpis.totalRevenue)}
                sub="Total de pedidos que cuentan como venta"
              />
              <KpiCard
                icon="📦"
                tone="primary"
                label="Pedidos"
                value={kpis.count}
                sub="Pedidos pagados: por preparar o completados"
              />
              <KpiCard
                icon="🎯"
                tone="primary"
                label="Ticket promedio"
                value={fmtMoney(kpis.avgTicket)}
                sub="Promedio vendido por pedido"
              />
              <KpiCard
                icon="🛍️"
                tone="success"
                label="Productos vendidos"
                value={totalQty}
                sub={`${grouped.length} ${plural(grouped.length, 'producto distinto', 'productos distintos')}`}
              />
              {showCashKpi && (
                <KpiCard
                  icon="💵"
                  tone="success"
                  label="Ventas en efectivo"
                  value={fmtMoney(paymentSummary.cashTotal)}
                  sub={`${paymentSummary.cashCount} ${plural(paymentSummary.cashCount, 'pedido', 'pedidos')}`}
                />
              )}
              {showTransferKpi && (
                <KpiCard
                  icon="🏦"
                  tone="primary"
                  label="Ventas por transferencia"
                  value={fmtMoney(paymentSummary.transferTotal)}
                  sub={`${paymentSummary.transferCount} ${plural(paymentSummary.transferCount, 'pedido', 'pedidos')}`}
                />
              )}
            </div>
          </section>

          <section className="sr-overview-grid">
            <div className="sr-card surface">
              <div className="sr-section-head">
                <div>
                  <h2 className="sr-card-title">Productos más vendidos</h2>
                  <p className="sr-card-sub helper-text">
                    Qué productos se movieron más en unidades y valor vendido.
                  </p>
                </div>
              </div>

              <div className="sr-card-metric">
                {groupedFiltered.length} {plural(groupedFiltered.length, 'producto distinto', 'productos distintos')} - {' '}
                {groupedFiltered.reduce((s, p) => s + p.qty, 0)} {plural(groupedFiltered.reduce((s, p) => s + p.qty, 0), 'unidad vendida', 'unidades vendidas')}
                {productFilter === 'beauty' && ` - ${bizName}`}
                {canalFilter !== 'all' && ` - ${canalLabel}`}
              </div>

              {groupedFiltered.length === 0 ? (
                <div className="sr-empty-note">
                  {productFilter === 'beauty'
                    ? `Aún no hay productos vendidos de ${bizName} para mostrar.`
                    : 'Aún no hay productos vendidos para mostrar.'}
                </div>
              ) : (
                <div className="sr-prod-table">
                  <div className="sr-prod-hd">
                    <span>#</span>
                    <span>Producto</span>
                    <span className="sr-align-c">Unidades</span>
                    <span className="sr-align-r">Valor vendido</span>
                    <span aria-hidden="true" />
                  </div>
                  {groupedFiltered.map((p, i) => {
                    const isOpen = expandedProductId === p.productId;
                    const hasVars = p.variations.length > 1;
                    return (
                      <div key={p.productId} className="sr-prod-block">
                        <div
                          className={`sr-prod-row${hasVars ? ' sr-prod-row--expandable' : ''}`}
                          onClick={hasVars ? () => setExpandedProductId(isOpen ? null : p.productId) : undefined}
                          role={hasVars ? 'button' : undefined}
                          tabIndex={hasVars ? 0 : undefined}
                          onKeyDown={hasVars ? e => { if (e.key === 'Enter' || e.key === ' ') setExpandedProductId(isOpen ? null : p.productId); } : undefined}
                        >
                          <span className="sr-prod-rank">{i + 1}</span>
                          <div className="sr-prod-info">
                            {/* fotos-resumen: un reintento vía i0.wp.com y después el
                                placeholder — el snapshot del pedido va directo al origen
                                y un hipo del hosting lo deja roto (censo 7-oct-2026). */}
                            <FotoProducto src={p.image} />

                            <div className="sr-prod-text">
                              <div className="sr-prod-name">{decodeHtml(p.name)}</div>
                              {!hasVars && p.variations[0]?.varLabel && (
                                <div className="sr-prod-var">{decodeHtml(p.variations[0].varLabel)}</div>
                              )}
                              {hasVars && (
                                <div className="sr-prod-var">{p.variations.length} {plural(p.variations.length, 'variación', 'variaciones')}</div>
                              )}
                            </div>
                          </div>
                          <span className="sr-prod-qty sr-align-c">{p.qty}</span>
                          <span className="sr-prod-rev sr-align-r">{fmtMoney(p.revenue)}</span>
                          {hasVars ? (
                            <span className={`sr-prod-chevron${isOpen ? ' sr-prod-chevron--open' : ''}`} aria-hidden="true" />
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </div>
                        {hasVars && (
                          <div className={`sr-var-rows${isOpen ? ' sr-var-rows--open' : ''}`} aria-hidden={!isOpen}>
                            <div className="sr-var-rows-inner">
                              {p.variations.map(v => {
                                const varPct = p.qty > 0 ? Math.round((v.qty / p.qty) * 100) : 0;
                                return (
                                  <div key={`${v.variationId}-${v.varLabel}`} className="sr-var-row">
                                    <span className="sr-var-label">{decodeHtml(v.varLabel) || 'Sin variación'}</span>
                                    <div className="sr-var-bar-wrap" title={`${varPct}% de las unidades de este producto`}>
                                      <div className="sr-var-bar-fill" style={{ width: `${varPct}%` }} />
                                    </div>
                                    <span className="sr-var-qty">{v.qty} uds.</span>
                                    <span className="sr-var-rev">{fmtMoney(v.revenue)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="sr-card surface">
              <div className="sr-section-head">
                <div>
                  <h2 className="sr-card-title">Medios de pago usados</h2>
                  <p className="sr-card-sub helper-text">Cuánto vendió cada forma de pago en este periodo.</p>
                </div>
              </div>

              {paymentSummary.list.length === 0 ? (
                <div className="sr-empty-note">No hay información suficiente para generar este resumen.</div>
              ) : (
                <div className="sr-payment-list">
                  {paymentSummary.list.map(payment => {
                    const pct = paymentSummary.total > 0 ? Math.round((payment.amount / paymentSummary.total) * 100) : 0;
                    return (
                      <div key={payment.method} className="sr-payment-row">
                        <div className="sr-payment-topline">
                          <div>
                            <strong>{payment.method}</strong>
                            <span>{payment.orders} {plural(payment.orders, 'pedido', 'pedidos')}</span>
                          </div>
                          <strong>{fmtMoney(payment.amount)}</strong>
                        </div>
                        <div className="sr-payment-bar" title={`${payment.method}: ${fmtMoney(payment.amount)} (${pct}%)`}>
                          <div className="sr-payment-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {productFilter === 'all' && (
            <section className="sr-card surface">
              <div className="sr-section-head">
                <div>
                  <h2 className="sr-card-title">Pedidos recientes</h2>
                  <p className="sr-card-sub helper-text">
                    Del más reciente al más antiguo, con cliente, pago, total y estado.
                    {canalFilter !== 'all' && ` Canal: ${canalLabel}.`}
                  </p>
                </div>
              </div>

              <OrderListInspector
                orders={visibleOrders}
                selected={selected}
                onSelect={setSelected}
                pagination={{ mode: 'loadMore', hasMore: hiddenCount > 0 && !showAll, loadingMore: false, onLoadMore: () => setShowAll(true) }}
                empty={{ icon: '🧾', title: 'Sin pedidos', description: 'No hay ventas en este período.' }}
                isVendedor={userRole === 'vendedor'}
                onEdit={handleEditOrder}
                onManageShipping={handleManageShipping}
                onOrderCancelled={handleOrderCancelled}
              />
            </section>
          )}
        </>
      )}
      {/* pos-pedir-celular: captura de datos del POS (semana × vendedora) — solo admin/coordinador */}
      <CapturaPosCard userRole={userRole} />
    </div>
    {fullPage && (
      <OrderEditPage
        order={fullPage.order}
        onBack={() => setFullPage(null)}
        onListChanged={() => fetchData(rangeStart, rangeEnd)}
      />
    )}
    </>
  );
}
